import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { superAdminRoleId } from "../../Utils/permission"
// REDUCER FUNCTIONS
import {
  refreshTokenFailure,
  refreshTokenRequest,
  refreshTokenSuccess,
  verifyTokenFailure,
  verifyTokenRequest,
  verifyTokenSuccess
} from "./reducerFunc"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  signInRequest: ["data"],
  signInSuccess: ["userInfo", "accessToken", "refreshToken"],
  signInFailure: ["error"],
  verify2FARequest: ["data"],
  verify2FAFailure: ["error"],
  generate2FASetupRequest: ["data"],
  generate2FASetupSuccess: ["payload"],
  generate2FASetupFailure: ["error"],
  confirm2FASetupRequest: ["data"],
  confirm2FASetupSuccess: [
    "userInfo",
    "accessToken",
    "refreshToken",
    "recoveryCodes",
    "commitToken"
  ],
  confirm2FASetupFailure: ["error"],
  clear2FASetupState: null,
  generateAdminSecurity2FARequest: null,
  generateAdminSecurity2FASuccess: ["payload"],
  generateAdminSecurity2FAFailure: ["error"],
  confirmAdminSecurity2FARequest: ["data"],
  confirmAdminSecurity2FASuccess: ["recoveryCodes", "commitToken"],
  confirmAdminSecurity2FAFailure: ["error"],
  commitAdminSecurity2FARequest: ["data"],
  commitAdminSecurity2FASuccess: null,
  commitAdminSecurity2FAFailure: ["error"],
  acceptAdminInviteRequest: ["data"],
  acceptAdminInviteSuccess: ["ephemeralToken"],
  acceptAdminInviteFailure: ["error"],

  checkEmailInitSuccess: ["code_valid"],

  refreshTokenRequest: ["payload"], // payload: { token }
  refreshTokenSuccess: ["payload"], // payload: { data }
  refreshTokenFailure: ["payload"], // payload: { errors }

  verifyTokenRequest: ["payload"], // payload: { token }
  verifyTokenSuccess: ["payload"], // payload: { data }
  verifyTokenFailure: ["payload"], // payload: { errors }

  authErrorReset: null,

  updateTokens: ["accessToken", "refreshToken"],

  resendVerificationLinkRequest: ["data"],

  createAccountRequest: ["data", "query", "isReferralOutsider"],
  createAccountSuccess: ["userInfo", "accessToken", "refreshToken"],
  createAccountFailure: ["error"],

  changeEmailRequest: ["data"],
  confirmEmailRequest: ["hash", "redirect"],
  sendConfirmEmailRequest: ["data"],
  sendConfirmEmailSuccess: ["time_to_next_try"],
  sendConfirmEmailFailure: ["error"],
  confirmEmailInitRequest: ["hash", "redirect"],

  checkPasswordRequest: ["email"],
  confirmPasswordRequest: ["code"],

  logout: null,
  clearState: null,

  updateProfile: ["data"],

  signUpRequest: ["password", "isNewUserCheck", "confirm_code"],
  signUpSuccess: ["message"],
  signUpFailure: ["error"],

  changeAdminPasswordRequest: ["payload"],
  changeAdminPasswordSuccess: ["message"],
  changeAdminPasswordFailure: ["error"],

  shopifyAuthRequest: ["shop"],

  socialAuthRequest: ["accessToken", "socialType", "user", "isReferralOutsider"],
  socialAuthSuccess: ["userInfo", "accessToken", "refreshToken"],
  socialAuthFailure: ["error"],

  socialAuthConnectRequest: ["accessToken", "socialType", "user"],
  socialAuthConnectReset: null,

  socialAccountSave: ["name"],

  socialAuthDeleteRequest: ["socialType"],
  socialAuthDeleteSuccess: ["userInfo"],
  socialAuthDeleteFailure: ["error"],

  socialAuthReset: null,

  setRemember: ["data"],

  setEmailSent: ["data"],

  authFetchingReset: null,

  createBillingDescriptorSuccess: null,

  setIsPasswordCreated: ["payload"],
  setShowModalPremiumFeatures: ["showModalPremiumFeatures"],

  clearAuthMessage: null,
  authSetProp: ["key", "value"]
})

export const AuthTypes = Types
export default Creators

/* ------------- Initial State ------------- */

export const INITIAL_STATE = Immutable({
  userInfo: null,
  codeValid: false,
  isPasswordCreated: false,
  accessToken: null,
  refreshToken: null,
  isValidRefreshToken: false,
  error: null,
  fetching: false,

  updateFetching: false,
  updateErrors: false,

  socialAccount: "",

  socialAuthConnectResult: null,
  socialAuthDeleteResult: null,

  isAdmin: false,
  role: null,

  emailSent: false,

  showModalPremiumFeatures: false,
  confirmEmailTimeToNextTry: null,

  rememberMe: null,
  message: null,

  tempEmail: null,
  ephemeralToken: null,
  is2FAConfigured: false,
  twoFactorSecret: null,
  twoFactorQrCode: null,
  twoFactorSetupToken: null,
  twoFactorRecoveryCodes: [],
  twoFactorCommitToken: null
})

/* ------------- Selectors ------------- */

export const AuthSelectors = {
  getUserInfo: (state) => state.auth.userInfo,
  getAccessToken: (state) => state.auth.accessToken,
  getRefreshToken: (state) => state.auth.refreshToken
}

/* ------------- Reducers ------------- */

export const setIsPasswordCreated = (state, { payload: data }) =>
  state.merge({ isPasswordCreated: data })

export const signInRequest = (state, { data }) => {
  const prevErrors = state?.error
  return state.merge({ fetching: true, error: { ...prevErrors } })
}

export const signInSuccess = (state, { userInfo, accessToken, refreshToken }) => {
  const isAdmin =
    userInfo?.role?.id === 2 || userInfo?.role?.id === 5 || userInfo?.role?.id === superAdminRoleId
  const role = userInfo?.role?.id
  return state.merge({
    fetching: false,
    error: null,
    userInfo,
    accessToken,
    refreshToken,
    isAdmin,
    role,
    ephemeralToken: null,
    is2FAConfigured: false,
    twoFactorSecret: null,
    twoFactorQrCode: null,
    twoFactorSetupToken: null,
    twoFactorRecoveryCodes: [],
    twoFactorCommitToken: null
  })
}

export const checkEmailInitSuccess = (state, { code_valid }) =>
  state.merge({ fetching: false, error: null, codeValid: code_valid })

export const signInFailure = (state, { error }) => {
  const prevErrors = state?.error
  return state.merge({ error: { ...prevErrors, ...error }, fetching: false })
}

export const verify2FARequest = (state) => state.merge({ fetching: true, error: null })

export const verify2FAFailure = (state, { error }) => state.merge({ error, fetching: false })

export const generate2FASetupRequest = (state) => state.merge({ fetching: true, error: null })

export const generate2FASetupSuccess = (state, { payload }) =>
  state.merge({
    fetching: false,
    error: null,
    twoFactorSecret: payload?.secret || null,
    twoFactorQrCode: payload?.qr_code || null,
    twoFactorSetupToken: payload?.setup_token || null,
    twoFactorRecoveryCodes: [],
    twoFactorCommitToken: null
  })

export const generate2FASetupFailure = (state, { error }) => state.merge({ error, fetching: false })

export const confirm2FASetupRequest = (state) => state.merge({ fetching: true, error: null })

export const confirm2FASetupSuccess = (
  state,
  { userInfo, accessToken, refreshToken, recoveryCodes, commitToken }
) => {
  const nextUserInfo = userInfo || state.userInfo
  const isAdmin =
    nextUserInfo?.role?.id === 2 ||
    nextUserInfo?.role?.id === 5 ||
    nextUserInfo?.role?.id === superAdminRoleId
  const role = nextUserInfo?.role?.id

  return state.merge({
    fetching: false,
    error: null,
    userInfo: nextUserInfo,
    accessToken: accessToken || state.accessToken,
    refreshToken: refreshToken || state.refreshToken,
    isAdmin,
    role,
    ephemeralToken: null,
    is2FAConfigured: true,
    twoFactorSecret: null,
    twoFactorQrCode: null,
    twoFactorSetupToken: null,
    twoFactorRecoveryCodes: recoveryCodes || [],
    twoFactorCommitToken: commitToken || null
  })
}

export const confirm2FASetupFailure = (state, { error }) => state.merge({ error, fetching: false })

export const clear2FASetupState = (state) =>
  state.merge({
    twoFactorSecret: null,
    twoFactorQrCode: null,
    twoFactorSetupToken: null,
    twoFactorRecoveryCodes: [],
    twoFactorCommitToken: null
  })

export const generateAdminSecurity2FARequest = (state) =>
  state.merge({ fetching: true, error: null })

export const generateAdminSecurity2FASuccess = (state, { payload }) =>
  state.merge({
    fetching: false,
    error: null,
    twoFactorSecret: payload?.secret || null,
    twoFactorQrCode: payload?.qr_code || null,
    twoFactorSetupToken: payload?.setup_token || null,
    twoFactorRecoveryCodes: [],
    twoFactorCommitToken: null
  })

export const generateAdminSecurity2FAFailure = (state, { error }) =>
  state.merge({ error, fetching: false })

export const confirmAdminSecurity2FARequest = (state) =>
  state.merge({ fetching: true, error: null })

export const confirmAdminSecurity2FASuccess = (state, { recoveryCodes, commitToken }) =>
  state.merge({
    fetching: false,
    error: null,
    twoFactorSetupToken: null,
    twoFactorQrCode: null,
    twoFactorSecret: null,
    twoFactorRecoveryCodes: recoveryCodes || [],
    twoFactorCommitToken: commitToken || null
  })

export const confirmAdminSecurity2FAFailure = (state, { error }) =>
  state.merge({ error, fetching: false })

export const commitAdminSecurity2FARequest = (state) => state.merge({ fetching: true, error: null })

export const commitAdminSecurity2FASuccess = (state) =>
  state.merge({ fetching: false, error: null })

export const commitAdminSecurity2FAFailure = (state, { error }) =>
  state.merge({ error, fetching: false })

export const acceptAdminInviteRequest = (state) => state.merge({ fetching: true, error: null })

export const acceptAdminInviteSuccess = (state, { ephemeralToken }) =>
  state.merge({
    fetching: false,
    error: null,
    ephemeralToken: ephemeralToken || null
  })

export const acceptAdminInviteFailure = (state, { error }) =>
  state.merge({ error, fetching: false })

export const createAccountRequest = (state, { data, query, isReferralOutsider }) => {
  return state.merge({ fetching: true, error: null })
}

export const createAccountSuccess = (state, { userInfo, accessToken, refreshToken }) => {
  const isAdmin =
    userInfo?.role?.id === 2 || userInfo?.role?.id === 5 || userInfo?.role?.id === superAdminRoleId
  const role = userInfo?.role?.id
  return state.merge({
    fetching: false,
    error: null,
    userInfo,
    accessToken,
    refreshToken,
    isAdmin,
    role
  })
}

export const createAccountFailure = (state, { error }) => {
  const prevErrors = state?.error
  return state.merge({ error: { ...prevErrors, ...error }, fetching: false })
}

export const authErrorReset = (state) => state.merge({ error: null, fetching: false })

export const updateTokens = (state, { accessToken, refreshToken }) =>
  state.merge({ accessToken, refreshToken })

export const resendVerificationLinkRequest = (state, { data }) =>
  state.merge({ fetching: true, error: null })

export const setEmailSent = (state, { data }) => state.merge({ fetching: false, emailSent: data })

export const changeEmailRequest = (state, { data }) => state.merge({ fetching: true, error: null })

export const confirmEmailRequest = (state, { hash, redirect }) =>
  state.merge({ fetching: true, error: null })

export const sendConfirmEmailRequest = (state) => state.merge({ fetching: true, error: null })

export const sendConfirmEmailSuccess = (state, { time_to_next_try }) =>
  state.merge({
    fetching: false,
    error: null,
    confirmEmailTimeToNextTry: time_to_next_try
  })

export const sendConfirmEmailFailure = (state, { error }) => state.merge({ error, fetching: false })

export const confirmEmailInitRequest = (state, { hash, redirect }) =>
  state.merge({ fetching: true, error: null })

export const checkPasswordRequest = (state, { email }) =>
  state.merge({ fetching: true, error: null })

export const confirmPasswordRequest = (state, { code }) =>
  state.merge({ fetching: true, error: null })

export const logout = (state) => {
  localStorage.removeItem("completely_finished")
  return state.merge({ ...INITIAL_STATE, error: state.error, rememberMe: state.rememberMe })
}

export const clearState = (state) =>
  state.merge({ ...INITIAL_STATE, isPasswordCreated: state.isPasswordCreated })

export const updateProfile = (state, { data }) => {
  let userInfo = {}
  if (data?.user) {
    userInfo = { ...state.userInfo, ...data.user }
  } else {
    userInfo = { ...state.userInfo, ...data }
  }

  return state.merge({ userInfo })
}

export const signUpRequest = (state, { password, isNewUserCheck, confirm_code }) =>
  state.merge({ fetching: true, error: null })

export const signUpSuccess = (state, { message }) =>
  state.merge({ fetching: false, error: null, isPasswordCreated: false, message })

export const signUpFailure = (state, { error }) => state.merge({ error, fetching: false })

export const changeAdminPasswordRequest = (state, { payload }) =>
  state.merge({ fetching: true, error: null })
export const changeAdminPasswordSuccess = (state, { message }) =>
  state.merge({ fetching: false, error: null, message })
export const changeAdminPasswordFailure = (state, { error }) =>
  state.merge({ error, fetching: false })

export const shopifyAuthRequest = (state, { accessToken, socialType, user }) =>
  state.merge({ fetching: true, error: null })

export const socialAuthRequest = (state, { accessToken, socialType, user }) =>
  state.merge({ fetching: true, error: null })

export const socialAuthConnectRequest = (state, { accessToken, socialType, user }) =>
  state.merge({ fetching: true, error: null })

export const socialAuthFailure = (state, { error }) => state.merge({ error, fetching: false })

export const socialAuthSuccess = (state, { userInfo, accessToken, refreshToken }) => {
  const isAdmin =
    userInfo?.role?.id === 2 || userInfo?.role?.id === 5 || userInfo?.role?.id === superAdminRoleId
  const role = userInfo?.role?.id
  return state.merge({
    fetching: false,
    error: null,
    ...(isOnboardingFinished(userInfo) && { socialAuthConnectResult: true }),
    userInfo,
    accessToken,
    refreshToken,
    isAdmin,
    role
  })
}

export const socialAccountSave = (state, { name }) => state.merge({ socialAccount: name })

export const socialAuthReset = (state) =>
  state.merge({
    fetching: false,
    error: null,
    socialAuthConnectResult: null,
    socialAuthDeleteResult: null,
    socialAccount: ""
  })

export const socialAuthDeleteRequest = (state, { socialType }) =>
  state.merge({ fetching: true, error: null })
export const socialAuthDeleteSuccess = (state, { userInfo }) => {
  const isAdmin =
    userInfo?.role?.id === 2 || userInfo?.role?.id === 5 || userInfo?.role?.id === superAdminRoleId
  const role = userInfo?.role?.id
  return state.merge({
    fetching: false,
    error: null,
    socialAuthDeleteResult: true,
    userInfo,
    isAdmin,
    role
  })
}

export const socialAuthDeleteFailure = (state, { error }) => state.merge({ error, fetching: false })

export const setRemember = (state, { data }) => state.merge({ rememberMe: data })

const createBillingDescriptorSuccess = (state) => {
  if (!isOnboardingFinished(state.userInfo)) {
    return state.merge({ userInfo: { ...state.userInfo, next_step: 7 } })
  } else {
    return state
  }
}

export const authFetchingReset = (state) =>
  state.merge({
    fetching: false,
    updateFetching: false
  })

export const setShowModalPremiumFeatures = (state, { showModalPremiumFeatures }) =>
  state.merge({ showModalPremiumFeatures })

export const clearAuthMessage = (state) => state.merge({ message: null })

export const authSetProp = (state, { key, value }) => {
  const obj = {}
  obj[key] = value
  return state.merge(obj)
}

/* ------------- Hookup Reducers To Types ------------- */

export const reducer = createReducer(INITIAL_STATE, {
  [Types.SIGN_IN_REQUEST]: signInRequest,
  [Types.SIGN_IN_SUCCESS]: signInSuccess,
  [Types.SIGN_IN_FAILURE]: signInFailure,
  [Types.VERIFY2_FA_REQUEST]: verify2FARequest,
  [Types.VERIFY2_FA_FAILURE]: verify2FAFailure,
  [Types.GENERATE2_FA_SETUP_REQUEST]: generate2FASetupRequest,
  [Types.GENERATE2_FA_SETUP_SUCCESS]: generate2FASetupSuccess,
  [Types.GENERATE2_FA_SETUP_FAILURE]: generate2FASetupFailure,
  [Types.CONFIRM2_FA_SETUP_REQUEST]: confirm2FASetupRequest,
  [Types.CONFIRM2_FA_SETUP_SUCCESS]: confirm2FASetupSuccess,
  [Types.CONFIRM2_FA_SETUP_FAILURE]: confirm2FASetupFailure,
  [Types.CLEAR2_FA_SETUP_STATE]: clear2FASetupState,
  [Types.GENERATE_ADMIN_SECURITY2_FA_REQUEST]: generateAdminSecurity2FARequest,
  [Types.GENERATE_ADMIN_SECURITY2_FA_SUCCESS]: generateAdminSecurity2FASuccess,
  [Types.GENERATE_ADMIN_SECURITY2_FA_FAILURE]: generateAdminSecurity2FAFailure,
  [Types.CONFIRM_ADMIN_SECURITY2_FA_REQUEST]: confirmAdminSecurity2FARequest,
  [Types.CONFIRM_ADMIN_SECURITY2_FA_SUCCESS]: confirmAdminSecurity2FASuccess,
  [Types.CONFIRM_ADMIN_SECURITY2_FA_FAILURE]: confirmAdminSecurity2FAFailure,
  [Types.COMMIT_ADMIN_SECURITY2_FA_REQUEST]: commitAdminSecurity2FARequest,
  [Types.COMMIT_ADMIN_SECURITY2_FA_SUCCESS]: commitAdminSecurity2FASuccess,
  [Types.COMMIT_ADMIN_SECURITY2_FA_FAILURE]: commitAdminSecurity2FAFailure,
  [Types.ACCEPT_ADMIN_INVITE_REQUEST]: acceptAdminInviteRequest,
  [Types.ACCEPT_ADMIN_INVITE_SUCCESS]: acceptAdminInviteSuccess,
  [Types.ACCEPT_ADMIN_INVITE_FAILURE]: acceptAdminInviteFailure,

  [Types.CREATE_ACCOUNT_REQUEST]: createAccountRequest,
  [Types.CREATE_ACCOUNT_SUCCESS]: createAccountSuccess,
  [Types.CREATE_ACCOUNT_FAILURE]: createAccountFailure,

  [Types.AUTH_ERROR_RESET]: authErrorReset,

  [Types.UPDATE_TOKENS]: updateTokens,

  [Types.RESEND_VERIFICATION_LINK_REQUEST]: resendVerificationLinkRequest,

  [Types.CHANGE_EMAIL_REQUEST]: changeEmailRequest,
  [Types.CONFIRM_EMAIL_REQUEST]: confirmEmailRequest,
  [Types.SEND_CONFIRM_EMAIL_REQUEST]: sendConfirmEmailRequest,
  [Types.SEND_CONFIRM_EMAIL_SUCCESS]: sendConfirmEmailSuccess,
  [Types.SEND_CONFIRM_EMAIL_FAILURE]: sendConfirmEmailFailure,
  [Types.CONFIRM_EMAIL_INIT_REQUEST]: confirmEmailInitRequest,
  [Types.CHECK_EMAIL_INIT_SUCCESS]: checkEmailInitSuccess,

  [Types.CHECK_PASSWORD_REQUEST]: checkPasswordRequest,
  [Types.CONFIRM_PASSWORD_REQUEST]: confirmPasswordRequest,

  [Types.LOGOUT]: logout,
  [Types.CLEAR_STATE]: clearState,

  [Types.SET_EMAIL_SENT]: setEmailSent,

  [Types.UPDATE_PROFILE]: updateProfile,

  [Types.SIGN_UP_REQUEST]: signUpRequest,
  [Types.SIGN_UP_SUCCESS]: signUpSuccess,
  [Types.SIGN_UP_FAILURE]: signUpFailure,

  [Types.CHANGE_ADMIN_PASSWORD_REQUEST]: changeAdminPasswordRequest,
  [Types.CHANGE_ADMIN_PASSWORD_SUCCESS]: changeAdminPasswordSuccess,
  [Types.CHANGE_ADMIN_PASSWORD_FAILURE]: changeAdminPasswordFailure,

  [Types.SHOPIFY_AUTH_REQUEST]: shopifyAuthRequest,

  [Types.SOCIAL_AUTH_REQUEST]: socialAuthRequest,
  [Types.SOCIAL_AUTH_SUCCESS]: socialAuthSuccess,
  [Types.SOCIAL_AUTH_FAILURE]: socialAuthFailure,

  [Types.SOCIAL_AUTH_DELETE_REQUEST]: socialAuthDeleteRequest,
  [Types.SOCIAL_AUTH_DELETE_SUCCESS]: socialAuthDeleteSuccess,
  [Types.SOCIAL_AUTH_DELETE_FAILURE]: socialAuthDeleteFailure,

  [Types.SET_REMEMBER]: setRemember,

  [Types.AUTH_FETCHING_RESET]: authFetchingReset,

  [Types.SOCIAL_AUTH_CONNECT_REQUEST]: socialAuthConnectRequest,
  [Types.SOCIAL_AUTH_RESET]: socialAuthReset,

  [Types.SOCIAL_ACCOUNT_SAVE]: socialAccountSave,

  [Types.SET_IS_PASSWORD_CREATED]: setIsPasswordCreated,

  [Types.REFRESH_TOKEN_REQUEST]: refreshTokenRequest,
  [Types.REFRESH_TOKEN_SUCCESS]: refreshTokenSuccess,
  [Types.REFRESH_TOKEN_FAILURE]: refreshTokenFailure,

  [Types.VERIFY_TOKEN_REQUEST]: verifyTokenRequest,
  [Types.VERIFY_TOKEN_SUCCESS]: verifyTokenSuccess,
  [Types.VERIFY_TOKEN_FAILURE]: verifyTokenFailure,

  [Types.CREATE_BILLING_DESCRIPTOR_SUCCESS]: createBillingDescriptorSuccess,

  [Types.SET_SHOW_MODAL_PREMIUM_FEATURES]: setShowModalPremiumFeatures,

  [Types.CLEAR_AUTH_MESSAGE]: clearAuthMessage,
  [Types.AUTH_SET_PROP]: authSetProp
})
